import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Search, Loader2, BookOpen } from "lucide-react";
import { formatPence, isValidMoney } from "./MaterialsCataloguePanel";

const API = `${window.location.origin}/api`;

const newKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const CataloguePicker = ({ open, onClose, onAdded, authHeaders, jobId }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState("1");
  const [override, setOverride] = useState("");
  const [idemKey, setIdemKey] = useState(newKey);
  const [adding, setAdding] = useState(false);

  const headers = { "Content-Type": "application/json", ...(authHeaders?.headers || {}) };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ status: "active", limit: "50" });
        if (search.trim()) params.set("search", search.trim());
        const res = await fetch(`${API}/admin/materials-catalogue?${params}`, { headers });
        const data = await res.json();
        if (!cancelled) setResults(data.items || []);
      } catch (e) {
        if (!cancelled) toast.error("Failed to search catalogue");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search]);

  useEffect(() => {
    if (open) { setSelected(null); setSearch(""); setQuantity("1"); setOverride(""); }
  }, [open]);

  const selectItem = (item) => {
    setSelected(item);
    setQuantity("1");
    setOverride("");
    setIdemKey(newKey()); // fresh key per selection — retries of the SAME add reuse it
  };

  const handleAdd = async () => {
    if (!selected) return;
    const qty = Number(quantity);
    if (!qty || qty <= 0) { toast.error("Quantity must be a positive number"); return; }
    if (override.trim() !== "" && !isValidMoney(override)) {
      toast.error("Price must be a positive amount with at most 2 decimal places"); return;
    }
    setAdding(true);
    try {
      const res = await fetch(`${API}/admin/materials-catalogue/${selected.id}/add-to-job`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          job_id: jobId,
          quantity: qty,
          unit_cost_override: override.trim() === "" ? null : override.trim(),
          idempotency_key: idemKey,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : (data.detail?.message || "Failed to add"));
        if (res.status === 409) setIdemKey(newKey());
        return;
      }
      toast.success(data.idempotent_replay ? "Already added (safe retry)" : `${selected.name} added to job`);
      onAdded?.();
    } catch (e) {
      toast.error("Failed to add");
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="catalogue-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="h-4 w-4" /> Add from catalogue</DialogTitle>
          <DialogDescription>A snapshot of the item is copied onto this job — later catalogue edits won't change it.</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input autoFocus placeholder="Search catalogue…" value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="catalogue-picker-search" />
        </div>

        <div className="max-h-52 overflow-y-auto space-y-1" data-testid="catalogue-picker-results">
          {loading ? (
            <div className="text-xs text-muted-foreground py-3 text-center">Searching…</div>
          ) : results.length === 0 ? (
            <div className="text-xs text-muted-foreground py-3 text-center" data-testid="catalogue-picker-empty">
              No active catalogue items found.
            </div>
          ) : results.map((item) => (
            <button key={item.id} type="button" onClick={() => selectItem(item)}
              className={`w-full text-left rounded-md border px-3 py-2 min-h-[44px] hover:bg-accent/20 transition-colors ${selected?.id === item.id ? "border-primary bg-primary/5" : ""}`}
              data-testid={`catalogue-picker-item-${item.id}`}>
              <div className="text-sm font-medium">{item.name}</div>
              <div className="text-xs text-muted-foreground">
                {[item.supplier, item.unit].filter(Boolean).join(" · ")} · Cost {formatPence(item.default_cost_price_pence)}
              </div>
            </button>
          ))}
        </div>

        {selected && (
          <div className="grid grid-cols-2 gap-2 border-t pt-3" data-testid="catalogue-picker-details">
            <div>
              <Label htmlFor="catalogue-picker-qty">Quantity</Label>
              <Input id="catalogue-picker-qty" type="number" step="0.01" min="0" value={quantity}
                onChange={(e) => setQuantity(e.target.value)} data-testid="catalogue-picker-qty" />
            </div>
            <div>
              <Label htmlFor="catalogue-picker-cost">£/unit (optional)</Label>
              <Input id="catalogue-picker-cost" inputMode="decimal"
                placeholder={((selected.default_cost_price_pence || 0) / 100).toFixed(2)}
                value={override} onChange={(e) => setOverride(e.target.value)}
                data-testid="catalogue-picker-cost-override" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="catalogue-picker-cancel">Cancel</Button>
          <Button onClick={handleAdd} disabled={!selected || adding} data-testid="catalogue-picker-add-btn">
            {adding ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Adding…</> : "Add to job"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CataloguePicker;
